import { useState, useRef, useEffect } from "react";
import { LogOut, ChevronDown, PhoneIncoming, KeyRound } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { usePermissions } from "@/lib/permissions/PermissionProvider";
import { Modal } from "@/components/ui/Modal";
import { MyTwoFactorCard } from "@/features/settings/SignInSecuritySection";

export function UserMenu() {
  const { user, signOut } = useAuth();
  const { isAdmin } = usePermissions();
  const [open, setOpen] = useState(false);
  // SETTINGS-ACCESS-01: your own sign-in security is personal — it lives
  // with your name, not in company Settings.
  const [signIn, setSignIn] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  // Derive initials from email for the avatar
  const initials = (user.email ?? "?").slice(0, 2).toUpperCase();
  // A name, not an address: full_name when the account has one, else the part
  // of the email before the @ (seths@… → "seths"). The full email still shows
  // inside the menu.
  const displayName: string =
    (user.user_metadata?.full_name as string | undefined) ?? (user.email ? user.email.split("@")[0] : "User");

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex h-10 items-center gap-2 rounded-xl pl-1.5 pr-2 text-[13.5px] font-medium text-slate-700 transition-colors",
          "hover:bg-slate-100",
          open && "bg-slate-100",
        )}
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-accent-500 to-brand-700 text-xs font-semibold text-white ring-2 ring-white">
          {initials}
        </div>
        <span className="hidden max-w-[140px] truncate lg:inline">
          {displayName}
        </span>
        <ChevronDown className={cn("h-3.5 w-3.5 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div data-glass="panel" className="absolute right-0 z-50 mt-1.5 w-60 overflow-hidden rounded-2xl">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="text-sm font-medium text-slate-900">
              {displayName}
            </div>
            <div className="truncate text-xs text-slate-500">{user.email}</div>
          </div>
          {/* Admin: the CALLS-00 simulated incoming call (used to float as a
              "Test ring" chip over every page's top-right — HD-SWEEP moved it
              here; CallDock listens for the event and shows the phone form). */}
          {isAdmin && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(new CustomEvent("pegasus-call-sim-open"));
              }}
              className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
            >
              <PhoneIncoming className="h-4 w-4 text-slate-400" />
              Simulate incoming call
              <span className="ml-auto text-[10.5px] font-semibold uppercase tracking-wide text-slate-400">admin</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); setSignIn(true); }}
            className="flex w-full items-center gap-2 border-b border-slate-100 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            <KeyRound className="h-4 w-4 text-slate-400" />
            My sign-in
            <span className="ml-auto text-[10.5px] text-slate-400">two-factor</span>
          </button>
          <button
            type="button"
            onClick={() => signOut()}
            className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      )}
      {signIn && (
        <Modal open onClose={() => setSignIn(false)} title="My sign-in" subtitle={user.email ?? undefined} size="md">
          <MyTwoFactorCard />
        </Modal>
      )}
    </div>
  );
}
